/**
 * This is the main implementation of the navigation component, only
 * missing the bits that require MooTools for implementation (those
 * are implemented in `./navigation` and passed into this component as
 * props.
 */
import { _js } from '@ifixit/utils/localize';
import { FaIcon } from '@ifixit/ui/components/icon';
import { trackAnalyticsCustomEvent } from '@ifixit/analytics';
import { useEffect, useRef } from 'react';

export function Navigation({ links, heading, pageTitle, onIfixit, isLoggedIn, tabsOnly }: any) {
   const visibleTabs = links.tabLinks.filter((tab: any) => tab.showTab);
   const headingText = heading ?? pageTitle;
   const currentTabRef = useRef<HTMLAnchorElement | null>(null);
   const stripRef = useRef<HTMLDivElement | null>(null);

   function getActiveTabIndex() {
      let index = 0;
      while (index < visibleTabs.length) {
         const tab = visibleTabs[index];
         if (tab.label === pageTitle) {
            break;
         }
         index++;
      }
      return index;
   }

   const activeTabIndex = getActiveTabIndex();
   const currentTabData = visibleTabs[activeTabIndex];
   const activeValue = visibleTabs[activeTabIndex]?.label ?? '';

   // Five tabs need 600px and a phone gives the strip about 375, so the
   // last two start off the right edge behind the fade. Arriving on one
   // of those from a link showed a strip with no lit tab in it at all.
   //
   // Driving scrollLeft rather than calling scrollIntoView: that scrolls
   // every scrollable ancestor, and `nearest` only means "the smallest
   // scroll", not "leave this axis alone". Landing part-way down one of
   // these pages, hydration would have yanked the document back up to
   // bring the strip into view. This touches one axis of one element.
   useEffect(() => {
      const tab = currentTabRef.current;
      const strip = stripRef.current;
      if (!tab || !strip) {
         return;
      }
      const tabBox = tab.getBoundingClientRect();
      const stripBox = strip.getBoundingClientRect();
      // Matches scroll-fade-x's gutter, so the tab lands past the ramp
      // rather than under the transparent end of it.
      const gutter = 16;
      if (tabBox.left < stripBox.left + gutter) {
         strip.scrollLeft -= stripBox.left + gutter - tabBox.left;
      } else if (tabBox.right > stripBox.right - gutter) {
         strip.scrollLeft += tabBox.right - stripBox.right + gutter;
      }
   }, []);

   return (
      <div id="communityNav" className="mt-6">
         <nav aria-label={_js('Community')}>
            {/* `py-1` is room for the tabs' focus ring, which the scroll
                box clips without it; `-my-1` gives it back, so the strip and
                its underline stay put. See `tabs.tsx`. */}
            <div ref={stripRef} className="scroll-fade-x -my-1 flex items-center py-1">
               {visibleTabs.map((tab: any) => (
                  <a
                     key={tab.label}
                     ref={tab.label === activeValue ? currentTabRef : undefined}
                     href={tab.url}
                     aria-current={tab.label === activeValue ? 'page' : undefined}
                     className="relative inline-flex min-w-[120px] flex-1 shrink-0 items-center justify-center whitespace-nowrap border-gray-200 border-b-2 px-5 py-2 font-semibold text-gray-500 text-sm no-underline transition-colors hover:border-gray-400 hover:text-gray-600 hover:no-underline focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 aria-[current=page]:border-brand-500 aria-[current=page]:text-brand-500"
                  >
                     {tab.label}
                  </a>
               ))}
            </div>
         </nav>
         {/* tabsOnly: the page's own React body renders the H1 and help
             link, so the strip is all this component contributes. */}
         {tabsOnly ? null : (
            <header className="mt-6 flex flex-wrap items-center justify-center md:justify-between">
               <h1 className="mb-1.5 w-full text-[30px] font-medium text-gray-800 md:mb-0 md:w-auto">
                  {headingText}
               </h1>
               <div className="hidden flex-auto flex-wrap justify-end gap-2 md:flex md:gap-4">
                  {onIfixit && (
                     <a
                        href={currentTabData?.help}
                        className="inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
                        onClick={() => {
                           trackAnalyticsCustomEvent({
                              eventCategory: pageTitle,
                              eventAction: `${pageTitle} - Btn "How this Works"`,
                              eventName: currentTabData?.help,
                           });
                        }}
                     >
                        {_js('How this Works')}
                        <FaIcon name="circle-info" className="size-4" />
                     </a>
                  )}
                  {!isLoggedIn && (
                     <a
                        href="/Join"
                        className="buttonLink inline-block"
                        onClick={() => {
                           trackAnalyticsCustomEvent({
                              eventCategory: pageTitle,
                              eventAction: `${pageTitle} - Btn - Join`,
                           });
                        }}
                     >
                        {_js('Join')}
                     </a>
                  )}
               </div>
            </header>
         )}
      </div>
   );
}
